import { useEffect, useRef, useState } from 'react';

interface LogViewerProps {
  maxLines?: number;
  className?: string;
}

export default function LogViewer({ maxLines = 200, className = '' }: LogViewerProps) {
  const [lines, setLines] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  useEffect(() => {
    const es = new EventSource('/api/log/stream');

    es.onopen = () => setConnected(true);

    es.onmessage = (event) => {
      setLines((prev) => {
        const next = [...prev, event.data];
        return next.length > maxLines ? next.slice(-maxLines) : next;
      });
    };

    es.onerror = () => {
      setConnected(false);
      es.close();
      // Reconnect after 5s
      setTimeout(() => {
        setLines((prev) => [...prev, '[LOG VIEWER] Reconnecting...']);
      }, 5000);
    };

    return () => es.close();
  }, [maxLines]);

  useEffect(() => {
    if (autoScrollRef.current && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [lines]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  };

  const getLineClass = (line: string) => {
    if (line.includes('ERROR')) return 'text-trader-loss';
    if (line.includes('WARNING')) return 'text-trader-warn';
    if (line.includes('INFO')) return 'text-trader-muted';
    return 'text-trader-border';
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs ${connected ? 'text-trader-profit' : 'text-trader-loss'}`}>
          {connected ? 'Live' : 'Disconnected'}
        </span>
        <button
          onClick={() => setLines([])}
          className="text-xs text-trader-muted hover:text-trader-text"
        >
          Clear
        </button>
      </div>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="bg-black/50 border border-trader-border rounded p-3 h-64 overflow-y-auto font-mono text-xs leading-relaxed"
      >
        {lines.length === 0 ? (
          <p className="text-trader-muted">Waiting for log events...</p>
        ) : (
          lines.map((line, i) => (
            <div key={i} className={`whitespace-pre-wrap break-all ${getLineClass(line)}`}>
              {line}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
